/**
 * Cliente HTTP de la API.
 *
 * ADR-006: GU requiere conexión. No hay reintentos en cola ni respuestas
 * cacheadas para servir offline — si falla la red, el guardia de
 * conectividad muestra la pantalla correspondiente y punto.
 *
 * Todo error de la API llega tipado con un `code` accionable, nunca como
 * un string suelto: la UI decide qué decir a partir del código, no
 * parseando mensajes.
 */

export interface ApiErrorBody {
  code: string;
  message: string;
  /**
   * Campos extra que algunos errores traen y la UI necesita para no ser un
   * callejón: `place_duplicate` manda el `existingId` del lugar que ya
   * está, y con él se puede ofrecer ir a recomendarlo en vez de dejar a la
   * persona con "ya existe" y nada que hacer.
   */
  [extra: string]: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  /** El cuerpo completo, para los errores que traen datos accionables. */
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(String(body.message));
    this.name = 'ApiError';
    this.code = String(body.code);
    this.status = status;
    this.body = body;
  }
}

/** Lo que la API devuelve por lugar cercano. */
export interface NearbyPlace {
  id: string;
  name: string;
  lat: number;
  lng: number;
  distanceM: number;
  foodTypes: string[];
  isPromoted: boolean;
  /**
   * Si el lugar lo creaste tú. Sin sesión, `false` para todos — no es un
   * error: sin sesión no hay "tuyo".
   *
   * Lo calcula el servidor comparando `created_by` con la sesión. Es un
   * booleano y NO `created_by` a propósito: `/places/nearby` es público, y
   * decir quién creó cada pin expondría un dato de personas en una app de
   * cien que se conocen.
   */
  isMine: boolean;
}

export interface NearbyResponse {
  places: NearbyPlace[];
  radiusM: number;
}

const BASE = '/api';

/** Más de esto se siente roto en móvil con datos. */
const TIMEOUT_MS = 8_000;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(`${BASE}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        // Solo cuando HAY cuerpo. Fastify responde 400
        // `FST_ERR_CTP_EMPTY_JSON_BODY` a un POST que anuncia JSON y no
        // manda nada — y `POST /invites/:code/check` es exactamente eso.
        // Los tests con `app.inject` no lo vieron porque no ponían la
        // cabecera; se descubrió abriendo la landing en un navegador.
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });

    if (!res.ok) {
      let body: ApiErrorBody = {
        code: 'unknown_error',
        message: 'Algo salió mal de nuestro lado.',
      };
      try {
        body = (await res.json()) as ApiErrorBody;
      } catch {
        /* respuesta sin cuerpo JSON: se queda el genérico */
      }
      throw new ApiError(res.status, body);
    }

    // 202 y 204 no traen cuerpo (`/events`, revocar invitación). Llamar a
    // `json()` sobre una respuesta vacía lanza, y sería un error inventado
    // por el cliente sobre una petición que salió bien.
    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return undefined as T;
    }

    const texto = await res.text();
    return (texto ? JSON.parse(texto) : undefined) as T;
  } finally {
    clearTimeout(timer);
  }
}

export interface NearbyParams {
  lat: number;
  lng: number;
  radiusM?: number;
  foodType?: string;
}

/**
 * Lugares cercanos.
 *
 * Durante la Era Pionera devolver una lista vacía es lo NORMAL, no un
 * error (ADR-007). Quien consuma esto debe distinguir "no hay lugares
 * todavía" de "falló la petición" — son estados muy distintos para el
 * usuario y la UI los trata distinto.
 */
export async function fetchNearby(p: NearbyParams): Promise<NearbyResponse> {
  const qs = new URLSearchParams({
    lat: String(p.lat),
    lng: String(p.lng),
  });
  if (p.radiusM) qs.set('radius', String(p.radiusM));
  if (p.foodType) qs.set('food_type', p.foodType);

  return request<NearbyResponse>(`/places/nearby?${qs.toString()}`);
}

// ───────────────────────────── Lugares ─────────────────────────────

/**
 * Pone un pin en el mapa.
 *
 * No exige Visit Token (ADR-013): un `Place` es un dato factual. Lanza
 * `ApiError` con `place_duplicate` —y `existingId` en el cuerpo— si ese
 * lugar ya está, para poder ofrecer ir a recomendarlo.
 */
export async function createPlace(input: {
  name: string;
  lat: number;
  lng: number;
  kind?: string;
  foodTypes?: string[];
}): Promise<{ id: string; name: string }> {
  return request('/places', { method: 'POST', body: JSON.stringify(input) });
}

export interface MyPlace {
  id: string;
  name: string;
  lat: number;
  lng: number;
  kind: string;
  createdAt: string;
  /** Si ya escribiste tu recomendación. Es lo que cuenta para invitar. */
  recommendedByMe: boolean;
}

export interface MyPlacesResponse {
  places: MyPlace[];
  /** Recomendaciones con token, no pines: ADR-013. */
  published: number;
  needed: number;
  invitesLeft: number;
}

export async function fetchMyPlaces(): Promise<MyPlacesResponse> {
  return request('/places/mine');
}

// ───────────────────── Invitación y sesión ─────────────────────

export interface InviteCheck {
  state: 'valid' | 'expired' | 'redeemed';
  /** Quién invita. `null` en Semilla Cero: a esos nadie los invitó. */
  inviter: {
    displayName: string;
    avatarUrl: string | null;
    ordinal: number | null;
  } | null;
  origin: 'user' | 'semilla';
  expiresAt: string;
}

/**
 * Valida un código sin canjearlo. Es lo que alimenta la landing.
 *
 * Lanza `ApiError` con `invite_not_found` si el código no existe. Ojo: un
 * pase vencido o ya usado NO lanza — responde con `state` y con quién
 * invita, porque la pantalla ofrece pedirle otro por WhatsApp.
 */
export async function checkInvite(code: string): Promise<InviteCheck> {
  return request<InviteCheck>(`/invites/${encodeURIComponent(code)}/check`, {
    method: 'POST',
  });
}

/** El perfil propio, tal como lo devuelve `GET /api/me`. */
export interface Me {
  id: string;
  displayName: string | null;
  avatarUrl: string | null;
  cityId: string | null;
  favoriteFoodTypes: string[];
  level: number;
  xp: number;
  reputationScore: number;
  /** Lo calcula el servidor: la PWA no lo deduce por su cuenta. */
  canPublish: boolean;
  invitesLeft: number;
  /**
   * "Pionero #014". `null` mientras no se otorgue.
   *
   * Durante la Era Pionera el nivel se congela (ADR-007) y este ordinal
   * es el estatus que de verdad importa.
   */
  pioneroOrdinal: number | null;
}

/**
 * ¿Hay sesión? Devuelve el perfil, o `null` si no la hay.
 *
 * El 401 no es una excepción aquí: "nadie ha entrado" es una respuesta
 * normal al arrancar la app, no un fallo que haya que capturar en cada
 * llamador. Cualquier otro error sí se propaga.
 */
export async function fetchMe(): Promise<Me | null> {
  try {
    return await request<Me>('/me');
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

/** Guarda nombre y avatar. El servidor toma el usuario de la sesión. */
export async function saveProfile(p: {
  displayName: string;
  avatarUrl: string;
}): Promise<Me> {
  return request<Me>('/me', { method: 'POST', body: JSON.stringify(p) });
}

/** Pide el código de acceso por correo. */
export async function requestOtp(email: string): Promise<void> {
  await request('/auth/email-otp/send-verification-otp', {
    method: 'POST',
    body: JSON.stringify({ email, type: 'sign-in' }),
  });
}

/**
 * Entra con el código. El pase viaja en la cabecera, no en el cuerpo.
 *
 * `x-gu-invite` y no la cookie porque **el código vive en la URL**
 * (`/i/ABC123`), que es la fuente de verdad: el navegador de WhatsApp
 * —por donde se reparten las invitaciones— rompe el contexto de cookies
 * al abrir en Safari o Chrome, y ahí la cookie no existiría.
 */
export async function signInWithOtp(p: {
  email: string;
  otp: string;
  inviteCode: string;
  deviceFp: string;
}): Promise<void> {
  await request('/auth/sign-in/email-otp', {
    method: 'POST',
    headers: { 'x-gu-invite': `${p.inviteCode}|${p.deviceFp}` },
    body: JSON.stringify({ email: p.email, otp: p.otp }),
  });
}

/**
 * Avisa de un evento del embudo. **Nunca lanza.**
 *
 * Medir no puede romper la pantalla que se está midiendo: si esto fallara
 * ruidosamente, un problema de red durante la landing —el momento de
 * máxima intención del producto— se convertiría en una pantalla rota.
 */
export async function trackEvent(
  name: string,
  inviteCode?: string,
): Promise<void> {
  try {
    await request('/events', {
      method: 'POST',
      body: JSON.stringify({ name, ...(inviteCode ? { inviteCode } : {}) }),
    });
  } catch {
    /* el embudo es para nosotros; el usuario está entrando */
  }
}

// ───────────────────── Visit Token ─────────────────────

export interface ClaimAbierto {
  claimId: string;
  nonce: string;
  profile: { radiusM: number; minDwellSec: number; maxAccuracyM: number };
  placeLocation: { lat: number; lng: number };
  startedAt: string;
}

export interface TokenEmitido {
  tokenId: string;
  band: 'green' | 'amber';
  expiresAt: string;
}

export interface PlaceDetail {
  id: string;
  name: string;
  lat: number;
  lng: number;
  kind: 'standard' | 'indoor' | 'street' | 'large_venue';
  address: string | null;
  foodTypes: string[];
  isPromoted: boolean;
  recommendationCount: number;
  /** Cuántas de esas llevan sello de visita (ADR-020). */
  sealedCount: number;
  mine?: {
    hasRecommended: boolean;
    activeTokenId: string | null;
    tokenExpiresAt: string | null;
  };
}

/**
 * La ficha de un lugar. Devuelve null si no existe, en vez de lanzar: que un
 * enlace compartido apunte a un lugar borrado no es un fallo de red.
 */
export async function fetchPlace(id: string): Promise<PlaceDetail | null> {
  try {
    return await request<PlaceDetail>(`/places/${id}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export async function startVisit(placeId: string): Promise<ClaimAbierto> {
  return request<ClaimAbierto>('/visits/start', {
    method: 'POST',
    body: JSON.stringify({ placeId }),
  });
}

export async function closeVisit(args: {
  claimId: string;
  nonce: string;
  samples: Array<{
    lat: number;
    lng: number;
    accuracyM: number;
    monotonicMs: number;
  }>;
}): Promise<TokenEmitido> {
  return request<TokenEmitido>('/visits/claim', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function publishRecommendation(
  placeId: string,
  /** `tokenId` opcional desde ADR-020: sin él se publica igual, sin sello. */
  args: { tokenId?: string; textoSagrado: string },
): Promise<{ id: string; qualifyingPlaces: number; proofLevel?: string }> {
  return request<{ id: string; qualifyingPlaces: number; proofLevel?: string }>(
    `/places/${placeId}/recommend`,
    { method: 'POST', body: JSON.stringify(args) },
  );
}

/* ─────────────────────── El Pacto (ADR-028) ─────────────────────── */

export interface Pact {
  version: string;
  bloques: readonly { titulo: string; texto: string }[];
}

/** Público: se lee en la puerta, antes de tener cuenta. */
export async function fetchPact(): Promise<Pact> {
  return request<Pact>('/pact');
}

export async function acceptPact(version: string): Promise<{ ok: true; version: string }> {
  return request<{ ok: true; version: string }>('/pact/accept', {
    method: 'POST',
    body: JSON.stringify({ version }),
  });
}

/* ──────────────────── La segunda puerta (ADR-021) ──────────────────── */

export interface AccessRequestInput {
  email: string;
  displayName: string;
  placePitch: string;
  placePitchWhy: string;
  deviceFp?: string;
}

export async function createAccessRequest(
  input: AccessRequestInput,
): Promise<{ id: string; state: string }> {
  return request<{ id: string; state: string }>('/access-requests', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/* ───────────────────── El panel de admin (ADR-024) ───────────────────── */

export interface AdminRequest {
  id: string;
  email: string;
  displayName: string;
  placePitch: string;
  placePitchWhy: string;
  state: string;
  createdAt: string;
}

export interface AdminSettings {
  invite_quota_default: number;
  invite_quota_first_place: number;
  signup_mode: string;
}

/**
 * La cola. Un 404 aquí NO significa "no hay cola": significa que quien pide
 * no es admin (ADR-024 — el 404 no anuncia la ruta). El llamador lo trata
 * como "esta pantalla no es para ti", nunca como un error.
 */
export async function fetchAdminRequests(state = 'pending'): Promise<AdminRequest[]> {
  const r = await request<{ requests: AdminRequest[] }>(`/admin/requests?state=${state}`);
  return r.requests;
}

export async function approveRequest(id: string): Promise<{ inviteCode: string }> {
  return request<{ inviteCode: string }>(`/admin/requests/${id}/approve`, { method: 'POST' });
}

export async function archiveRequest(id: string): Promise<void> {
  await request<void>(`/admin/requests/${id}/archive`, { method: 'POST' });
}

export async function fetchAdminSettings(): Promise<AdminSettings> {
  return request<AdminSettings>('/admin/settings');
}

export async function writeAdminSetting(key: string, value: unknown): Promise<AdminSettings> {
  return request<AdminSettings>('/admin/settings', {
    method: 'POST',
    body: JSON.stringify({ key, value }),
  });
}
